import React, { useState, useEffect } from 'react';
import { useTranslation } from './i18n/LanguageContext';

const DashboardPage = ({ API, isDarkMode, userToken, hasPaidPlan, appSettings, setCurrentView, setInitialProfileTab }) => {
  const { t } = useTranslation();

  // App-shell caches: read from localStorage synchronously so the dashboard
  // renders cards (avatar, name, streak, progress, calendar, goals) from
  // frame 1 on Ctrl+R instead of showing the skeleton then popping into
  // the real layout. Background fetches still run and silently update.
  const _readCache = (key) => {
    try { const c = localStorage.getItem(key); if (c) return JSON.parse(c); } catch {}
    return null;
  };
  const _cachedProfile = _readCache('dashProfileCache');
  const _cachedStats = _readCache('dashStatsCache');
  const _cachedGoals = _readCache('dashGoalsCache');
  const _cachedCalendar = _readCache('dashCalendarCache');
  const _cachedAnnouncements = _readCache('dashAnnouncementsCache');
  const _cachedProgress = _readCache('dashProgressCache');
  const _hasAnyCache = !!(_cachedProfile || _cachedStats || _cachedGoals || _cachedCalendar || _cachedProgress);

  const [profileData, setProfileData] = useState(_cachedProfile);
  const [learningStats, setLearningStats] = useState(_cachedStats);
  const [todayGoals, setTodayGoals] = useState(_cachedGoals);
  const [calendarData, setCalendarData] = useState(_cachedCalendar);
  const [announcements, setAnnouncements] = useState(_cachedAnnouncements || []);
  const [progressData, setProgressData] = useState(_cachedProgress);

  // If we have any cached data, skip the loading skeleton + fade-in entirely.
  // Cards appear instantly with cached values; fetch refreshes silently.
  const [loading, setLoading] = useState(!_hasAnyCache);
  const [animateIn, setAnimateIn] = useState(_hasAnyCache);

  // Greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return t('greeting_morning');
    if (hour >= 12 && hour < 17) return t('greeting_afternoon');
    return t('greeting_evening');
  };

  // Level system
  const getLevelInfo = (totalWords) => {
    const levels = [
      { name: 'Beginner', min: 0, max: 10, icon: '🌱', color: '#10b981', bg: 'bg-emerald-500' },
      { name: 'Elementary', min: 10, max: 50, icon: '📗', color: '#3b82f6', bg: 'bg-blue-500' },
      { name: 'Intermediate', min: 50, max: 200, icon: '📈', color: '#8b5cf6', bg: 'bg-violet-500' },
      { name: 'Advanced', min: 200, max: 500, icon: '🏆', color: '#ef4444', bg: 'bg-red-500' },
      { name: 'Expert', min: 500, max: 1000, icon: '👑', color: '#f59e0b', bg: 'bg-amber-500' },
    ];
    const level = levels.find(l => totalWords >= l.min && totalWords < l.max) || levels[levels.length - 1];
    const progress = Math.min(100, ((totalWords - level.min) / (level.max - level.min)) * 100);
    const wordsToNext = level.max - totalWords;
    return { ...level, progress, wordsToNext };
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Avatar URL helper (Dropbox transform)
  const getAvatarUrl = () => {
    const url = profileData?.avatar?.image_url;
    if (!url) return null;
    if (url.startsWith('/')) return `${API.replace(/\/api$/, '')}${url}`;
    return url.replace('?dl=0', '?raw=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com');
  };

  // API calls — each writes to cache after success so next refresh renders instantly
  const _writeCache = (key, data) => { try { localStorage.setItem(key, JSON.stringify(data)); } catch {} };

  const loadProfile = async () => {
    try {
      const res = await fetch(`${API}/user/profile`, { headers: { 'Authorization': `Bearer ${userToken}` } });
      if (res.ok) { const d = await res.json(); setProfileData(d); _writeCache('dashProfileCache', d); }
    } catch (e) { console.error('Error loading profile:', e); }
  };

  const loadLearningStats = async () => {
    try {
      const res = await fetch(`${API}/user/words/learned/stats`, { headers: { 'Authorization': `Bearer ${userToken}` } });
      if (res.ok) { const d = await res.json(); setLearningStats(d); _writeCache('dashStatsCache', d); }
    } catch (e) { console.error('Error loading stats:', e); }
  };

  const loadTodayGoals = async () => {
    try {
      const userDate = new Date().toISOString().split('T')[0];
      const res = await fetch(`${API}/user/goals/today?user_date=${userDate}`, { headers: { 'Authorization': `Bearer ${userToken}` } });
      if (res.ok) { const d = await res.json(); setTodayGoals(d); _writeCache('dashGoalsCache', d); }
    } catch (e) { console.error('Error loading goals:', e); }
  };

  const loadCalendarData = async () => {
    try {
      const now = new Date();
      const res = await fetch(`${API}/user/words/learned/calendar?month=${now.getMonth() + 1}&year=${now.getFullYear()}`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      if (res.ok) { const d = await res.json(); setCalendarData(d); _writeCache('dashCalendarCache', d); }
    } catch (e) { console.error('Error loading calendar data:', e); }
  };

  const loadAnnouncements = async () => {
    try {
      const res = await fetch(`${API}/announcements`);
      if (res.ok) { const d = await res.json(); setAnnouncements(d); _writeCache('dashAnnouncementsCache', d); }
    } catch (e) { console.error('Error loading announcements:', e); }
  };


  const loadProgress = async () => {
    try {
      const res = await fetch(`${API}/progress`, { headers: { 'Authorization': `Bearer ${userToken}` } });
      if (res.ok) { const d = await res.json(); setProgressData(d); _writeCache('dashProgressCache', d); }
    } catch (e) { console.error('Error loading progress:', e); }
  };


  useEffect(() => {
    if (userToken) {
      const fetches = [loadProfile(), loadLearningStats(), loadTodayGoals(), loadCalendarData(), loadAnnouncements(), loadProgress()];
      Promise.all(fetches)
        .finally(() => {
          setLoading(false);
          setTimeout(() => setAnimateIn(true), 50);
        });
    }
  }, [userToken]);

  // Announcement style
  const getAnnouncementStyle = (type) => {
    const styles = {
      info: { icon: 'ℹ️', dot: 'bg-blue-500' },
      warning: { icon: '⚠️', dot: 'bg-orange-500' },
      success: { icon: '✅', dot: 'bg-green-500' },
      update: { icon: '🔔', dot: 'bg-purple-500' },
      event: { icon: '📅', dot: 'bg-red-500' },
    };
    return styles[type] || styles.info;
  };

  const totalWords = learningStats?.total_words_learned || 0;
  const currentStreak = learningStats?.current_streak || 0;
  const thisWeek = learningStats?.this_week || 0;
  const level = getLevelInfo(totalWords);
  const avatarUrl = getAvatarUrl();

  // Calendar helpers
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const todayDate = now.getDate();
  const monthName = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const getCalendarDays = () => {
    const firstDay = new Date(currentYear, currentMonth, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const activeDatesSet = new Set(calendarData?.active_dates || []);

    const days = [];
    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null });
    }
    // Actual days
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push({
        day: d,
        isToday: d === todayDate,
        isActive: activeDatesSet.has(dateStr),
        isFuture: d > todayDate,
      });
    }
    return days;
  };

  const calendarDays = getCalendarDays();
  const activeDaysCount = calendarDays.filter(d => d.isActive).length;
  const missedDaysCount = calendarDays.filter(d => d.day && !d.isActive && !d.isToday && !d.isFuture).length;

  // Card base class
  const cardClass = `rounded-2xl border ${isDarkMode ? 'bg-gray-800 border-gray-700/60' : 'bg-white border-gray-200/80'} shadow-sm`;

  return (
    <div className={`min-h-screen pb-24 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Sticky Header */}
      <div className={`sticky top-[60px] lg:top-0 z-10 backdrop-blur-md ${isDarkMode ? 'bg-gray-900/95 border-gray-700' : 'bg-white/95 border-gray-200'} border-b`}>
        <div className="max-w-6xl mx-auto px-4 py-3">
          {setCurrentView && (
            <button onClick={() => setCurrentView('translator')}
              className={`flex items-center gap-1 mb-2 px-2 py-1 rounded-lg text-sm font-medium transition-all ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('dashboard_back')}
            </button>
          )}
          <div className="flex items-center gap-3">
            <span className="text-2xl">📊</span>
            <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('dashboard_title')}</h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">

        {/* Loading Skeleton — matches the actual layout below to prevent layout shift on data arrival */}
        {loading ? (
          <div className="space-y-5 animate-pulse">
            {/* ROW 1: Welcome + (Upgrade or Weather) */}
            <div className={`grid grid-cols-1 ${!hasPaidPlan ? 'md:grid-cols-3' : ''} gap-4`}>
              <div className={`${!hasPaidPlan ? 'md:col-span-2' : ''} h-28 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`} />
              {!hasPaidPlan && <div className={`h-28 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`} />}
            </div>
            {/* ROW 2: Your Activity (single card with 4 stat slots inside) */}
            <div className={`h-28 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`} />
            {/* ROW 2.5: Word Bank card */}
            <div className={`h-48 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`} />
            {/* ROW 3: Calendar + Daily Goals (stacked vertically) */}
            <div className={`h-72 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`} />
            <div className={`h-40 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`} />
          </div>
        ) : (
          <>
            {/* ═══ ROW 1: WELCOME + UPGRADE/WEATHER ═══ */}
            <div className={`grid grid-cols-1 ${!hasPaidPlan ? 'md:grid-cols-3' : ''} gap-4 transition-all duration-500 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {/* Welcome Card */}
              <div className={`${!hasPaidPlan ? 'md:col-span-2' : ''} ${cardClass} p-5 md:p-6`}>
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div className={`w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover object-top"
                        onError={(e) => { e.target.style.display = 'none'; if(e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                    ) : null}
                    <span className={`text-2xl md:text-3xl ${avatarUrl ? 'hidden' : 'flex'}`}>
                      👤
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className={`text-xl md:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {getGreeting()}, {profileData?.full_name?.split(' ')[0] || t('dashboard_there_fallback')}! 👋
                    </h2>
                    <p className={`text-sm mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{formatDate()}</p>
                  </div>
                  {/* Streak badge */}
                  {currentStreak > 0 && (
                    <div className={`hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl ${isDarkMode ? 'bg-orange-900/30 border border-orange-700/40' : 'bg-orange-50 border border-orange-200'}`}>
                      <span className="text-xl">🔥</span>
                      <div>
                        <p className={`text-lg font-black leading-none ${isDarkMode ? 'text-orange-300' : 'text-orange-600'}`}>{currentStreak}</p>
                        <p className={`text-xs font-medium ${isDarkMode ? 'text-orange-400/70' : 'text-orange-500/70'}`}>{t('dashboard_day_streak')}</p>
                      </div>
                    </div>
                  )}
                </div>
                {/* Mobile streak */}
                {currentStreak > 0 && (
                  <div className={`sm:hidden mt-3 flex items-center gap-2 px-3 py-2 rounded-xl w-fit ${isDarkMode ? 'bg-orange-900/30 border border-orange-700/40' : 'bg-orange-50 border border-orange-200'}`}>
                    <span className="text-lg">🔥</span>
                    <p className={`text-sm font-bold ${isDarkMode ? 'text-orange-300' : 'text-orange-600'}`}>{currentStreak} {t('dashboard_day_streak')}</p>
                  </div>
                )}
              </div>

              {/* Upgrade Card (free users) OR Weather (paid users) */}
              {!hasPaidPlan ? (
                <div className="rounded-2xl p-5 md:p-6 bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 shadow-lg shadow-purple-500/20 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">💎</span>
                      <h3 className="text-white font-bold text-lg">{t('dashboard_go_pro')}</h3>
                    </div>
                    <p className="text-white/80 text-sm leading-relaxed">{t('dashboard_go_pro_description')}</p>
                  </div>
                  <button
                    onClick={() => { setCurrentView('profile'); setInitialProfileTab('membership'); }}
                    className="mt-4 w-full py-2.5 rounded-xl bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-semibold text-sm transition-all active:scale-95 border border-white/20">
                    {t('dashboard_upgrade_now')}
                  </button>
                </div>
              ) : null}
            </div>

            {/* ═══ ANNOUNCEMENTS ═══ */}
            {announcements.length > 0 && (
              <div className={`${cardClass} p-5 md:p-6 transition-all duration-500 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <h3 className={`text-base font-bold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  <span>📢</span> {t('announcements')}
                </h3>
                <div className="space-y-2.5">
                  {announcements.map((a) => {
                    const style = getAnnouncementStyle(a.notification_type || 'info');
                    return (
                      <div key={a.id}
                        className={`flex items-start gap-3 p-3 rounded-xl ${isDarkMode ? 'bg-gray-750 hover:bg-gray-700/80' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`}>
                        <span className="text-lg flex-shrink-0 mt-0.5">{style.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{a.title}</p>
                          <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{a.content}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ═══ ROW 2: YOUR ACTIVITY ═══ */}
            <div className={`transition-all duration-500 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <div className={`${cardClass} p-4 md:p-5`}>
                <h3 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t('dashboard_your_activity')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-base ${isDarkMode ? 'bg-blue-900/40' : 'bg-blue-50'}`}>{'\u{1F4DA}'}</div>
                    <div>
                      <p className={`text-lg font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{progressData?.learnedWords?.length || 0}</p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('dashboard_words_saved')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-base ${isDarkMode ? 'bg-purple-900/40' : 'bg-purple-50'}`}>{'\u{1F9E9}'}</div>
                    <div>
                      <p className={`text-lg font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{progressData?.puzzlesCompleted || 0}</p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('dashboard_puzzles_done')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-base ${isDarkMode ? 'bg-green-900/40' : 'bg-green-50'}`}>{'\u{1F4C5}'}</div>
                    <div>
                      <p className={`text-lg font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{totalWords}</p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('dashboard_daily_words')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-base ${isDarkMode ? 'bg-amber-900/40' : 'bg-amber-50'}`}>{'\u{2B50}'}</div>
                    <div>
                      <p className={`text-lg font-black ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>{progressData?.puzzlePoints || 0}</p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('dashboard_points')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ═══ ROW 2.5: WORD BANK CARD ═══ */}
            <div className={`transition-all duration-500 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {hasPaidPlan ? (
                  <div className={`${cardClass} p-5 md:p-6`}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        {t('word_bank')}
                      </h3>
                    </div>

                    {/* Stats row */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="text-center">
                        <p className={`text-2xl font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {progressData?.learnedWords?.length || progressData?.wordsLearned || 0}
                        </p>
                        <p className={`text-xs font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('dashboard_words_saved')}</p>
                      </div>
                      <div className="text-center">
                        <p className={`text-2xl font-black ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                          {progressData?.puzzlePoints || 0}
                        </p>
                        <p className={`text-xs font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('dashboard_points')}</p>
                      </div>
                      <div className="text-center">
                        <p className={`text-2xl font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {progressData?.earnedBadges?.length || 0}
                        </p>
                        <p className={`text-xs font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('dashboard_badges')}</p>
                      </div>
                    </div>

                    {/* Next badge progress */}
                    {(() => {
                      const earnedIds = new Set([
                        ...(progressData?.badges || []),
                        ...(progressData?.earnedBadges || []).map(eb => eb.id)
                      ]);
                      const nextBdg = progressData?.allBadges
                        ?.filter(b => !earnedIds.has(b.id))
                        ?.sort((a, b) => a.threshold - b.threshold)?.[0];
                      if (!nextBdg) return null;
                      const pts = progressData?.puzzlePoints || 0;
                      const pct = Math.min((pts / nextBdg.threshold) * 100, 100);
                      return (
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {t('dashboard_next_label')} {nextBdg.icon} {nextBdg.name}
                            </span>
                            <span className={`text-xs font-medium ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                              {pts} / {nextBdg.threshold} {t('dashboard_pts')}
                            </span>
                          </div>
                          <div className={`w-full h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                            <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-1000"
                              style={{ width: animateIn ? `${pct}%` : '0%' }} />
                          </div>
                        </div>
                      );
                    })()}

                    {/* Earned badge icons */}
                    {progressData?.earnedBadges?.length > 0 && (
                      <div className="flex gap-2 mb-4 flex-wrap">
                        {progressData.earnedBadges.map((b) => (
                          <span key={b.id} className="text-2xl" title={b.name}>{b.icon}</span>
                        ))}
                      </div>
                    )}

                    {/* Play button */}
                    <button
                      onClick={() => setCurrentView('progress')}
                      className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 transition-all active:scale-[0.98] shadow-md text-sm"
                    >
                      {t('dashboard_play_word_bank')}
                    </button>

                    {(progressData?.learnedWords?.length || 0) === 0 && (progressData?.wordsLearned || 0) === 0 && (
                      <p className={`text-xs text-center mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {t('dashboard_save_words_hint')}
                      </p>
                    )}
                  </div>
                ) : (
                  /* Free user — blurred with upgrade overlay */
                  <div className="relative">
                    <div className={`${cardClass} p-5 md:p-6 overflow-hidden`} style={{ filter: 'blur(4px)', pointerEvents: 'none' }}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('word_bank')}</h3>
                      <div className="grid grid-cols-3 gap-3">
                        {[1,2,3].map(i => (
                          <div key={i} className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            <div className="text-center">
                              <div className={`h-6 rounded ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'} w-8 mx-auto mb-1`} />
                              <div className={`h-3 rounded ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'} w-12 mx-auto`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/20 rounded-2xl backdrop-blur-[1px]">
                      <span className="text-3xl mb-2">🔒</span>
                      <p className={`font-bold text-sm mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('dashboard_word_bank_locked_title')}</p>
                      <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t('dashboard_word_bank_locked_description')}</p>
                      <button
                        onClick={() => { setCurrentView('profile'); setInitialProfileTab('membership'); }}
                        className="px-5 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold text-sm hover:from-violet-700 hover:to-purple-700 transition-all active:scale-95 shadow-lg">
                        {t('upgrade_to_pro')}
                      </button>
                    </div>
                  </div>
                )}
            </div>

            {/* ═══ ROW 3: Calendar + Daily Goals ═══ */}
            <div className={`space-y-4 transition-all duration-500 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                {/* Learning Calendar */}
                <div className={`${cardClass} p-5 md:p-6`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-base font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      <span>📅</span> {t('dashboard_learning_calendar')}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className={`whitespace-nowrap text-[11px] font-semibold px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>
                        {activeDaysCount} {t('learned').toLowerCase()}
                      </span>
                      {missedDaysCount > 0 && (
                        <span className={`whitespace-nowrap text-[11px] font-semibold px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-red-900/40 text-red-400' : 'bg-red-50 text-red-500'}`}>
                          {missedDaysCount} {t('dashboard_missed').toLowerCase()}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {t('dashboard_calendar_description')}
                  </p>

                  {/* Month label */}
                  <p className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{monthName}</p>

                  {/* Day headers */}
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                      <div key={i} className={`text-center text-xs font-semibold py-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {d}
                      </div>
                    ))}
                  </div>

                  {/* Calendar grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {calendarDays.map((cell, i) => {
                      const isMissed = cell.day && !cell.isActive && !cell.isToday && !cell.isFuture;
                      return (
                        <div key={i} className={`relative flex items-center justify-center aspect-square rounded-lg text-xs font-medium transition-all ${
                          !cell.day
                            ? ''
                            : cell.isToday
                              ? cell.isActive
                                ? isDarkMode
                                  ? 'bg-emerald-900/50 border border-emerald-500/50 text-emerald-300'
                                  : 'bg-emerald-100 border border-emerald-400 text-emerald-700'
                                : isDarkMode
                                  ? 'bg-violet-600/30 border border-violet-500/50 text-violet-300'
                                  : 'bg-violet-100 border border-violet-300 text-violet-700'
                              : cell.isActive
                                ? isDarkMode
                                  ? 'bg-emerald-900/40 text-emerald-300'
                                  : 'bg-emerald-50 text-emerald-700'
                                : isMissed
                                  ? isDarkMode
                                    ? 'bg-red-900/30 text-red-400'
                                    : 'bg-red-50 text-red-400'
                                  : isDarkMode ? 'text-gray-600' : 'text-gray-300'
                        }`}>
                          {cell.day && (
                            <>
                              <span>{cell.day}</span>
                              {cell.isActive && (
                                <svg className={`absolute bottom-0.5 w-4 h-4 drop-shadow-sm ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Legend */}
                  <div className={`flex items-center gap-4 mt-3 pt-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                    <div className="flex items-center gap-1.5">
                      <div className={`w-2.5 h-2.5 rounded-full ${isDarkMode ? 'bg-emerald-500' : 'bg-emerald-500'}`} />
                      <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('learned')}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className={`w-2.5 h-2.5 rounded-full ${isDarkMode ? 'bg-red-500' : 'bg-red-400'}`} />
                      <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('dashboard_missed')}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className={`w-2.5 h-2.5 rounded-full ${isDarkMode ? 'bg-violet-500' : 'bg-violet-500'}`} />
                      <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('today')}</span>
                    </div>
                  </div>
                </div>

                {/* Daily Goals */}
                <div className={`${cardClass} p-5 md:p-6`}>
                  <h3 className={`text-base font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    <span>🎯</span> {t('dashboard_daily_goals')}
                  </h3>
                  <div className="space-y-4">
                    {/* Word Goal */}
                    <div>
                      <div className="flex justify-between items-center mb-1.5">
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t('dashboard_words_today')}</span>
                        <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {todayGoals?.words_learned_today || 0}/{todayGoals?.daily_word_goal || 3}
                        </span>
                      </div>
                      <div className={`w-full h-2.5 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-violet-500 transition-all duration-1000"
                          style={{ width: animateIn ? `${Math.min(100, ((todayGoals?.words_learned_today || 0) / (todayGoals?.daily_word_goal || 3)) * 100)}%` : '0%' }} />
                      </div>
                    </div>
                    {/* Words This Week */}
                    <div>
                      <div className="flex justify-between items-center mb-1.5">
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t('dashboard_words_this_week')}</span>
                        <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {todayGoals?.words_this_week || 0}/{todayGoals?.weekly_word_goal || 21}
                        </span>
                      </div>
                      <div className={`w-full h-2.5 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-1000 delay-200"
                          style={{ width: animateIn ? `${Math.min(100, ((todayGoals?.words_this_week || 0) / (todayGoals?.weekly_word_goal || 21)) * 100)}%` : '0%' }} />
                      </div>
                    </div>
                    {todayGoals?.goals_completed && (
                      <div className={`flex items-center gap-2 p-2.5 rounded-xl ${isDarkMode ? 'bg-green-900/30 border border-green-700/30' : 'bg-green-50 border border-green-200'}`}>
                        <span>🎉</span>
                        <span className="text-green-500 font-semibold text-sm">{t('dashboard_goals_completed')}</span>
                      </div>
                    )}
                  </div>
                </div>
            </div>



          </>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
